/**
 * 导入中心
 * ------------------------------------------------------------
 * 三步强制流程（不允许直接入库）：
 *   ① 上传 PDF/TXT → 本地解析（可选 AI 补全）
 *   ② AI 识别结果预览页：逐格可改、可删、可标记确认
 *   ③ 人工确认后才 commitImport() 写入正式内容库
 *
 * 这个页面的存在就是为了保证「AI 不确定的内容不会被当成事实」。
 */
import { state } from '../core/store.js';
import { parseDocument, pdfToText } from '../ai/pdfParser.js';
import { extractDraft, mergeDrafts } from '../ai/extractor.js';
import { enrichDraft, loadLLMConfig } from '../ai/llmAdapter.js';
import { saveDraft, getDraft, listDrafts, commitImport, deleteDraft, discardDraft } from '../services/importer.js';
import { listTextbooks, listUnits, invalidateCache } from '../services/dataSource.js';
import { card, emptyState, tag, confirmDialog, openModal, closeModal } from '../components/ui.js';
import { editableTable, bindEditableTable, summaryBar } from '../components/editor.js';
import { navigate } from '../core/router.js';
import { escapeHtml, toast, fmtDate, nowISO } from '../utils/index.js';
import { emit, EVENTS } from '../core/events.js';

const KIND_OPTIONS = [
  { value: 'textbook', label: '教材（单词/句子/语法）' },
  { value: 'workbook', label: '练习册（题目为主）' },
  { value: 'paper', label: '单元卷（题目）' },
];

let currentDraft = null;   // 模块级：预览页里的草稿

export default async function renderImport(mount, params = {}) {
  if (params.draft) return renderPreview(mount, params.draft);

  const [, drafts, llm, libRes] = await Promise.all([null, listDrafts(), loadLLMConfig(), fetch('./data/library.json').catch(() => null)]);
  const pending = drafts.filter(d => d.status === 'pending_review');
  const done = drafts.filter(d => d.status !== 'pending_review').slice(0, 6);
  const library = libRes?.ok ? await libRes.json() : null;

  mount.innerHTML = `
    <div class="page-head">
      <h1>📥 导入中心</h1>
      <p class="lead">上传教材/练习册 PDF → AI 识别 → <b>必须人工确认</b> 才会正式入库。识别不确定的内容会标记「待确认」，绝不自动补全。</p>
    </div>

    <div class="split">
      <div class="col" style="gap:16px">
        ${card(`
          <div class="drop-zone" id="drop">
            <div class="dz-icon">📄</div>
            <div class="dz-title">把 PDF / TXT 拖到这里，或点击选择文件</div>
            <div class="dz-desc">支持 .pdf .txt .md · 文字版 PDF 识别效果最好；扫描件需要 OCR（当前版本不支持）</div>
            <input type="file" id="file" accept=".pdf,.txt,.md,application/pdf,text/plain" multiple hidden>
          </div>
          <div class="form-grid mt-8">
            <label>导入类型
              <select class="input" id="kind">
                ${KIND_OPTIONS.map(o => `<option value="${o.value}">${o.label}</option>`).join('')}
              </select>
            </label>
            <label>归属教材
              <select class="input" id="textbook">
                <option value="">（导入后新建教材）</option>
              </select>
            </label>
          </div>
          <div class="row mt-8" style="gap:8px">
            <button class="btn btn-primary" id="btn-pick" type="button">选择文件</button>
            <span class="muted" style="font-size:12px;align-self:center">
              AI 补全：${llm.enabled && llm.apiKey ? '<span class="sym-ok">已开启</span>' : '<span class="muted">未开启（到设置里配置）</span>'}
            </span>
          </div>
          <div id="progress" class="mt-8"></div>
        `, { title: '① 上传文件' })}

        ${card(
          pending.length
            ? `<div class="draft-list">
                ${pending.map(d => `
                  <div class="draft-row">
                    <div class="grow">
                      <div style="font-size:13.5px;font-weight:600">
                        ${escapeHtml(d.unit?.title ? `Unit ${d.unit.no} ${d.unit.title}` : '未命名单元')}
                        ${d.stats?.needsReview ? `<span class="tag warn">${d.stats.needsReview} 处待确认</span>` : ''}
                      </div>
                      <div class="muted" style="font-size:11.5px">
                        ${escapeHtml(d.sourceFile || '')} · ${d.stats?.words || 0} 词 / ${d.stats?.sentences || 0} 句 / ${d.stats?.questions || 0} 题 · ${fmtDate(d.createdAt)}
                      </div>
                    </div>
                    <button class="btn btn-sm btn-primary" data-open="${d.id}" type="button">查看预览</button>
                    <button class="btn btn-sm btn-ghost" data-drop="${d.id}" type="button">放弃</button>
                  </div>`).join('')}
              </div>`
            : emptyState('📭', '没有待确认的草稿', '上传文件后会先生成草稿，等你确认'),
          { title: `② 待确认的草稿（${pending.length}）` })}
      </div>

      <div class="col" style="gap:16px">
        ${card(`
          <ol class="steps">
            <li><b>上传</b>：PDF 在浏览器本地解析，不上传服务器</li>
            <li><b>识别</b>：按版式规则抽取单词/句子/语法/题目</li>
            <li><b>预览</b>：逐格修改，不确定的会标黄</li>
            <li><b>确认入库</b>：只有这一步会写入正式题库</li>
          </ol>`, { title: '导入流程' })}

        ${card(`
          <p class="muted" style="font-size:12.5px">
            如果 PDF 是扫描图片（没有任何文字层），系统会明确提示"几乎没有文字层"，
            此时请改用文字版 PDF，或手工在预览页录入。
          </p>
          <p class="muted" style="font-size:12.5px">
            AI 补全默认关闭。开启后模型只被允许"填空"，不允许编造 —— 输出仍然标记为待确认。
          </p>`, { title: '识别能力说明' })}

        ${done.length ? card(`
          <div class="draft-list">
            ${done.map(d => `
              <div class="draft-row">
                <div class="grow">
                  <div style="font-size:13px">${escapeHtml(d.sourceFile || '')}</div>
                  <div class="muted" style="font-size:11.5px">${d.status === 'committed' ? '已导入' : '已放弃'} · ${fmtDate(d.updatedAt || d.createdAt)}</div>
                </div>
                <button class="icon-btn" data-remove="${d.id}" type="button" title="删除记录">🗑</button>
              </div>`).join('')}
          </div>`, { title: '最近处理记录' }) : ''}

        ${library ? card(`
          <div class="draft-list">
            ${library.items
              .filter(i => i.ocr?.done && i.type === 'exercise')
              .map(i => `
                <div class="draft-row">
                  <div class="grow">
                    <div style="font-size:13.5px;font-weight:600">${escapeHtml(i.title)}</div>
                    <div class="muted" style="font-size:11.5px">
                      ${i.ocr.pages} 页 · ${i.confirmed ? '<span class="tag green">已确认</span>' : '<span class="tag warn">待核对</span>'}
                      ${i.hasTextLayer ? '· 文字版' : '· 扫描版'}
                    </div>
                  </div>
                  <button class="btn btn-sm btn-primary" data-ocr="${i.id}" type="button">
                    ${i.confirmed ? '查看' : '进入 OCR 核对'}
                  </button>
                </div>`).join('') || `<div class="muted" style="font-size:12px;padding:8px">暂无可核对的扫描版 PDF。上传后先用后端 OCR 处理即可在此看到。</div>`}
          </div>`, { title: '📄 资料库（OCR 已完成）' }) : ''}
      </div>
    </div>`;

  // 教材下拉
  const books = await listTextbooks();
  const sel = mount.querySelector('#textbook');
  books.forEach(b => {
    const o = document.createElement('option');
    o.value = b.id;
    o.textContent = b.name;
    sel.appendChild(o);
  });
  if (state.selection.textbookId) sel.value = state.selection.textbookId;

  // 文件选择
  const drop = mount.querySelector('#drop');
  const fileInput = mount.querySelector('#file');
  mount.querySelector('#btn-pick').onclick = () => fileInput.click();
  drop.onclick = () => fileInput.click();
  drop.addEventListener('dragover', e => { e.preventDefault(); drop.classList.add('over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('over'));
  drop.addEventListener('drop', e => {
    e.preventDefault();
    drop.classList.remove('over');
    handleFiles([...e.dataTransfer.files], mount);
  });
  fileInput.onchange = () => handleFiles([...fileInput.files], mount);

  mount.querySelectorAll('[data-open]').forEach(b => {
    b.addEventListener('click', () => navigate('#/import', { draft: b.dataset.open }));
  });
  mount.querySelectorAll('[data-drop]').forEach(b => {
    b.addEventListener('click', async () => {
      await discardDraft(b.dataset.drop);
      toast('已放弃该草稿');
      navigate('#/import');
    });
  });
  mount.querySelectorAll('[data-remove]').forEach(b => {
    b.addEventListener('click', async () => {
      await deleteDraft(b.dataset.remove);
      toast('记录已删除');
      navigate('#/import');
    });
  });
  mount.querySelectorAll('[data-ocr]').forEach(b => {
    b.addEventListener('click', () => navigate(`#/ocr/${b.dataset.ocr}`));
  });
}

/* ------------------------------ 上传与识别 ------------------------------ */

async function handleFiles(files, mount) {
  const usable = files.filter(f => /\.(pdf|txt|md|csv)$/i.test(f.name) || f.type.startsWith('text/') || f.type === 'application/pdf');
  if (!usable.length) return toast('请选择 PDF / TXT / MD 文件', 'warn');

  const prog = mount.querySelector('#progress');
  const kind = mount.querySelector('#kind').value;
  const textbookId = mount.querySelector('#textbook').value;

  prog.innerHTML = `<div class="progress"><i style="width:8%"></i></div><p class="muted mt-4" style="font-size:12px">正在解析…</p>`;

  try {
    const drafts = [];
    const fileErrors = [];
    for (const file of usable) {
      try {
        const parsed = await parseDocument(file, ({ page, total }) => {
          const pct = Math.round((page / total) * 80);
          prog.innerHTML = `<div class="progress"><i style="width:${pct}%"></i></div>
            <p class="muted mt-4" style="font-size:12px">解析 ${escapeHtml(file.name)} 第 ${page}/${total} 页…</p>`;
        });

      if (!parsed.hasText) {
        const tpl = [
          '# 教材：人教PEP 四年级上册',
          '# Unit 1 My classroom',
          '',
          '[单词]',
          'window 窗户 There is a window. 教室里有一扇窗户。',
          'door 门 Please open the door. 请开门。',
          '',
          '[句子]',
          'What is in the classroom?|教室里有什么？',
          'Let me clean the desks.|让我来擦桌子。',
          '',
          '[语法]',
          'there be 句型|There is a door. / There are two doors.',
        ].join('\n');
        prog.innerHTML = `<div class="feedback no"><div class="feedback-head">⚠ 这个文件几乎没有文字层</div>
          <div class="feedback-line">${escapeHtml(file.name)} 是<b>扫描图片版 PDF</b>（每一页都是照片，选不中文字），
          当前版本不做 OCR，所以提取不到内容（字符数：${parsed.textLength}）。<br>
          <b>推荐做法：</b>把单词 / 句子按下面的模板存成 .txt 文件再导入，效果一样好：</div>
          <pre class="tpl-pre">${escapeHtml(tpl)}</pre>
          <div class="tpl-actions"><button class="btn btn-sm" id="btn-copy-tpl">📋 复制模板</button>
          <span class="muted" style="font-size:12px;align-self:center">按模板填好后，像平常一样拖进这里即可</span></div></div>`;
        prog.querySelector('#btn-copy-tpl')?.addEventListener('click', async () => {
          try { await navigator.clipboard.writeText(tpl); toast('模板已复制，粘贴到记事本即可', 'success'); }
          catch { toast('复制失败，请手动选中复制', 'warn'); }
        });
        continue;
      }

      let draft = extractDraft(parsed, { textbookId, kind });

      // 可选：AI 补全缺失字段（只填空，不覆盖）
      const llm = await loadLLMConfig();
      if (llm.enabled && llm.apiKey) {
        prog.innerHTML = `<div class="progress"><i style="width:88%"></i></div><p class="muted mt-4" style="font-size:12px">AI 补全缺失字段…</p>`;
        try {
          const { draft: enriched, changed } = await enrichDraft(draft, llm);
          draft = enriched;
          if (changed) toast(`AI 补全了 ${changed} 个字段，请核对`, 'warn');
        } catch (err) {
          console.warn('[import] AI 补全失败', err);
          toast('AI 补全失败：' + err.message, 'warn');
        }
      }
      drafts.push(draft);
      } catch (err) {
        // 单个文件失败只提示该文件，不拖累其他文件
        console.error('[import] 文件解析失败', file.name, err);
        prog.insertAdjacentHTML('beforeend', `<div class="feedback no"><div class="feedback-head">
          ✗ ${escapeHtml(file.name)} 解析失败</div>
          <div class="feedback-line">${escapeHtml(err.message || String(err))}<br>
          请确认文件没有损坏；扫描版 PDF 无法提取文字，可改用 .txt 导入。</div></div>`);
      }
    }

    if (!drafts.length) return;

    const merged = drafts.length > 1 ? mergeDrafts(drafts) : drafts[0];
    await saveDraft(merged);
    prog.innerHTML = '';

    toast(`识别完成：${merged.stats.words} 个单词 / ${merged.stats.sentences} 句`, 'success');
    navigate('#/import', { draft: merged.id });
  } catch (err) {
    console.error(err);
    prog.innerHTML = `<div class="feedback no"><div class="feedback-head">解析失败</div>
      <div class="feedback-line">${escapeHtml(err.message || String(err))}</div></div>`;
  }
}

/* ------------------------------ 预览与编辑 ------------------------------ */

async function renderPreview(mount, draftId) {
  const draft = await getDraft(draftId);
  if (!draft) {
    mount.innerHTML = emptyState('🔍', '草稿不存在', '可能已被删除', '<a class="btn btn-primary" href="#/import">返回导入中心</a>');
    return;
  }
  currentDraft = draft;

  const books = await listTextbooks();
  const units = draft.textbookId ? await listUnits(draft.textbookId) : [];

  mount.innerHTML = `
    <div class="page-head">
      <h1>🔍 识别结果预览</h1>
      <p class="lead">请逐项核对以下内容。<b>标黄的表示 AI 不确定</b>，确认无误后点「确认入库」。未确认的内容不会进入正式题库。</p>
    </div>

    ${draft.warnings?.length ? `<div class="feedback warn" style="margin-bottom:14px">
      ${draft.warnings.map(w => `<div class="feedback-line">⚠ ${escapeHtml(w)}</div>`).join('')}
    </div>` : ''}

    ${card(`
      <div class="form-grid">
        <label>单元编号
          <input class="input" id="u-no" type="number" min="1" max="30" value="${draft.unit?.no || 1}">
        </label>
        <label>单元标题
          <input class="input" id="u-title" value="${escapeHtml(draft.unit?.title || '')}" placeholder="例如 My classroom">
        </label>
        <label>单元主题
          <input class="input" id="u-theme" value="${escapeHtml(draft.unit?.theme || '')}" placeholder="例如 教室与学校场所">
        </label>
      </div>
      <div class="row mt-8" style="gap:10px;flex-wrap:wrap;align-items:center">
        <span class="muted" style="font-size:12px">来源文件：${escapeHtml(draft.sourceFile || '—')} · ${draft.pageCount || 0} 页</span>
      </div>
      ${summaryBar(draft)}`, { title: '① 单元信息' })}

    <div class="tab-bar mt-8" data-tabs>
      <button class="tab on" data-tab="words">单词 ${draft.words.length}</button>
      <button class="tab" data-tab="sentences">句子 ${draft.sentences.length}</button>
      <button class="tab" data-tab="grammar">语法 ${draft.grammar.length}</button>
      <button class="tab" data-tab="questions">题目 ${draft.questions.length}</button>
      <button class="tab" data-tab="raw">原文对照</button>
    </div>

    <div data-panel="words" id="panel-words"></div>
    <div data-panel="sentences" id="panel-sentences" hidden></div>
    <div data-panel="grammar" id="panel-grammar" hidden></div>
    <div data-panel="questions" id="panel-questions" hidden></div>
    <div data-panel="raw" hidden>
      <pre class="raw-pre" id="raw-pre">（原文仅在本次会话中可用）</pre>
    </div>

    <div class="sticky-actions">
      <button class="btn btn-ghost" id="btn-back" type="button">← 返回</button>
      <div class="grow"></div>
      <button class="btn btn-ghost" id="btn-add-word" type="button">＋ 加一条单词</button>
      <button class="btn btn-primary btn-lg" id="btn-commit" type="button">✓ 确认入库</button>
    </div>`;

  /* ---- 各面板 ---- */
  const wordsCols = [
    { field: 'en', label: '英文', width: '130px' },
    { field: 'phonetic', label: '音标', width: '120px' },
    { field: 'pos', label: '词性', width: '70px' },
    { field: 'zh', label: '中文' },
    { field: 'phrase', label: '短语', width: '150px' },
    { field: 'phraseZh', label: '短语中文', width: '120px' },
    { field: 'sentence', label: '例句', width: '200px' },
  ];
  const sentCols = [
    { field: 'en', label: '英文句子', type: 'textarea' },
    { field: 'zh', label: '中文翻译', type: 'textarea' },
    { field: 'pattern', label: '句型', width: '120px' },
  ];
  const gramCols = [
    { field: 'category', label: '分类（需人工确认）', width: '130px' },
    { field: 'rule', label: '规则说明', type: 'textarea' },
    { field: 'examples', label: '例句', type: 'tags' },
  ];
  const qCols = [
    { field: 'type', label: '题型', width: '90px' },
    { field: 'prompt', label: '题干', type: 'textarea' },
    { field: 'options', label: '选项', type: 'tags' },
    { field: 'answer', label: '答案', type: 'tags', width: '140px' },
    { field: 'explain', label: '解析', width: '160px' },
  ];

  const paint = () => {
    const panel = (id, key, cols, rows) => {
      const box = mount.querySelector(id);
      if (!box) return;
      box.innerHTML = card(editableTable({ key, columns: cols, rows }), {});
      bindEditableTable(box, {
        key,
        rows,
        onChange: (i, field, value) => {
          draft[key][i][field] = value;
          if (draft[key][i].needsReview && value) {
            // 改了内容就算人工确认过这一格
            draft[key][i].lastEditedAt = nowISO();
          }
          persistLater();
        },
        onDelete: (i) => {
          draft[key].splice(i, 1);
          recalc();
          paint();
          persistLater();
        },
        onToggle: (i) => {
          draft[key][i].needsReview = !draft[key][i].needsReview;
          paint();
          persistLater();
        },
      });
    };

    panel('#panel-words', 'words', wordsCols, draft.words);
    panel('#panel-sentences', 'sentences', sentCols, draft.sentences);
    panel('#panel-grammar', 'grammar', gramCols, draft.grammar);
    panel('#panel-questions', 'questions', qCols, draft.questions);
  };

  const recalc = () => {
    draft.stats = {
      words: draft.words.length,
      sentences: draft.sentences.length,
      grammar: draft.grammar.length,
      questions: draft.questions.length,
      needsReview: [...draft.words, ...draft.sentences, ...draft.grammar, ...draft.questions]
        .filter(x => x.needsReview).length,
    };
    const bar = mount.querySelector('.preview-stats');
    if (bar) bar.outerHTML = summaryBar(draft);
  };

  let timer = null;
  const persistLater = () => {
    clearTimeout(timer);
    timer = setTimeout(() => { recalc(); saveDraft(draft).catch(() => {}); }, 400);
  };

  paint();
  recalc();

  /* ---- Tab ---- */
  const tabs = mount.querySelector('[data-tabs]');
  tabs.querySelectorAll('.tab').forEach(t => {
    t.addEventListener('click', () => {
      tabs.querySelectorAll('.tab').forEach(x => x.classList.toggle('on', x === t));
      mount.querySelectorAll('[data-panel]').forEach(p => { p.hidden = p.dataset.panel !== t.dataset.tab; });
    });
  });

  /* ---- 单元信息 ---- */
  const bindUnit = (id, field) => {
    const input = mount.querySelector(id);
    input?.addEventListener('change', () => {
      draft.unit = draft.unit || {};
      draft.unit[field] = field === 'no' ? Number(input.value) : input.value;
      saveDraft(draft).catch(() => {});
    });
  };
  bindUnit('#u-no', 'no');
  bindUnit('#u-title', 'title');
  bindUnit('#u-theme', 'theme');

  /* ---- 加一条 ---- */
  mount.querySelector('#btn-add-word').onclick = () => {
    draft.words.unshift({ id: `manual-${Date.now()}`, en: '', zh: '', phonetic: '', pos: '', phrase: '', phraseZh: '', sentence: '', sentenceZh: '', needsReview: false });
    recalc();
    paint();
    saveDraft(draft).catch(() => {});
    mount.querySelector('[data-panel="words"] input')?.focus();
  };

  /* ---- 确认入库 ---- */
  mount.querySelector('#btn-commit').onclick = async () => {
    const need = draft.stats.needsReview;
    const empty = draft.words.filter(w => !w.en).length
      + draft.sentences.filter(s => !s.en).length
      + draft.questions.filter(q => !q.answer?.length).length;

    if (empty > 0) {
      const ok = await confirmDialog({
        title: '还有不完整的内容',
        message: `有 ${empty} 条内容缺少关键字段（英文或答案），它们会被自动跳过、不导入。是否继续？`,
        okText: '继续导入',
      });
      if (!ok) return;
    }

    if (need > 0) {
      const ok = await confirmDialog({
        title: `还有 ${need} 条内容标记为「待确认」`,
        message: '「待确认」表示 AI 识别不确定。建议逐条核对。仍要导入吗？未确认的内容会一并导入，但会被标记。',
        okText: '仍然导入',
        cancelText: '我先核对',
      });
      if (!ok) return;
    }

    try {
      const res = await commitImport(draft.id, { textbookId: draft.textbookId || undefined });
      toast(`导入完成：${res.counts.words} 词 / ${res.counts.sentences} 句 / ${res.counts.questions} 题`, 'success');
      invalidateCache(res.textbookId, res.unitId);
      emit(EVENTS.CONTENT_CHANGED, res);
      navigate('#/textbooks', { textbookId: res.textbookId, unit: res.unitId });
    } catch (err) {
      console.error(err);
      toast('导入失败：' + err.message, 'error');
    }
  };

  mount.querySelector('#btn-back').onclick = () => navigate('#/import');
}
